'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
import ReactECharts, { type EChartsOption } from 'echarts-for-react';
import { useCallback, useEffect, useMemo } from 'react';
import { formatterBreakdownChart, getChartAxisLabelByGranularity, getCorrectGranularity, getMonthAbbreviationToolTip, getSelectMetricText, replaceAllNumberLetOneBeforeDot } from '@/modules/finances/lib/breakdown-chart-utils';
import { formatBudgetName, removeBudgetWord } from '@/modules/finances/lib/utils';
import { useMediaQuery } from '@/modules/shared/hooks/use-media-query';
import { usLocalizedNumber } from '@/modules/shared/lib/humanization';
import { useFinancesYear } from '../../hooks/use-finances-year';
import type { AnalyticMetric, BarChartSeries, BreakdownChartSeriesData, GRANULARITY_OPTIONS } from '../../types';
interface BreakdownChartProps {
  year: string;
  selectedGranularity: GRANULARITY_OPTIONS;
  series: BreakdownChartSeriesData[];
  refBreakDownChart: React.RefObject<EChartsOption>;
  selectedMetric?: AnalyticMetric;
}
function BreakdownChart(t0) {
  const $ = _c(116);
  const {
    refBreakDownChart,
    series,
    selectedGranularity,
    selectedMetric
  } = t0;
  const {
    year
  } = useFinancesYear();
  let t1;
  if ($[0] !== selectedGranularity) {
    t1 = getCorrectGranularity(selectedGranularity);
    $[0] = selectedGranularity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const granularity = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      to: "sm"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isLessMobile = useMediaQuery(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      from: "sm",
      to: "md"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isMobile = useMediaQuery(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      from: "md",
      to: "lg"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const isTablet = useMediaQuery(t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      from: "lg",
      to: "xl"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const isDesktop1024 = useMediaQuery(t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      from: "xl",
      to: "2xl"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const isDesktop1280 = useMediaQuery(t6);
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      from: "2xl"
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const isDesktop1440 = useMediaQuery(t7);
  const isMobileOrLess = isMobile || isLessMobile;
  const showLineYear = (isMobile || isLessMobile) && (granularity === "monthly" || granularity === "quarterly");
  let t8;
  if ($[8] !== isDesktop1024 || $[9] !== isDesktop1280 || $[10] !== isDesktop1440 || $[11] !== isLessMobile || $[12] !== isMobile || $[13] !== isTablet) {
    t8 = () => {
      if (isLessMobile || isMobile) {
        return 170;
      }
      if (isTablet) {
        return 230;
      }
      if (isDesktop1024) {
        return 225;
      }
      if (isDesktop1280) {
        return 312;
      }
      if (isDesktop1440) {
        return 314;
      }
      return 312;
    };
    $[8] = isDesktop1024;
    $[9] = isDesktop1280;
    $[10] = isDesktop1440;
    $[11] = isLessMobile;
    $[12] = isMobile;
    $[13] = isTablet;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const getHeightGrid = t8;
  let t9;
  if ($[15] !== isDesktop1024 || $[16] !== isDesktop1280 || $[17] !== isDesktop1440 || $[18] !== isLessMobile || $[19] !== isMobile || $[20] !== isTablet) {
    t9 = () => {
      if (isLessMobile || isMobile || isTablet) {
        return 10;
      }
      if (isDesktop1024) {
        return 30;
      }
      if (isDesktop1280 || isDesktop1440) {
        return 11;
      }
      return 11;
    };
    $[15] = isDesktop1024;
    $[16] = isDesktop1280;
    $[17] = isDesktop1440;
    $[18] = isLessMobile;
    $[19] = isMobile;
    $[20] = isTablet;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const getTopGrid = t9;
  let t10;
  if ($[22] !== isDesktop1024 || $[23] !== isDesktop1280 || $[24] !== isDesktop1440 || $[25] !== isMobile || $[26] !== isTablet) {
    t10 = () => {
      if (isMobile) {
        return 4;
      }
      if (isTablet || isDesktop1024) {
        return 0;
      }
      if (isDesktop1280 || isDesktop1440) {
        return 4;
      }
      return 4;
    };
    $[22] = isDesktop1024;
    $[23] = isDesktop1280;
    $[24] = isDesktop1440;
    $[25] = isMobile;
    $[26] = isTablet;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const getRightGrid = t10;
  let t11;
  if ($[28] !== isDesktop1024 || $[29] !== isDesktop1280 || $[30] !== isDesktop1440 || $[31] !== isLessMobile || $[32] !== isMobile || $[33] !== isTablet) {
    t11 = () => {
      if (isLessMobile || isMobile) {
        return 5;
      }
      if (isTablet) {
        return 8;
      }
      if (isDesktop1024) {
        return 24;
      }
      if (isDesktop1280) {
        return 30;
      }
      if (isDesktop1440) {
        return 32;
      }
      return 36;
    };
    $[28] = isDesktop1024;
    $[29] = isDesktop1280;
    $[30] = isDesktop1440;
    $[31] = isLessMobile;
    $[32] = isMobile;
    $[33] = isTablet;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const getMarginYAxis = t11;
  let t12;
  if ($[35] !== isDesktop1024 || $[36] !== isDesktop1280 || $[37] !== isDesktop1440 || $[38] !== isMobile || $[39] !== isTablet) {
    t12 = () => {
      if (isMobile || isTablet) {
        return 12;
      }
      if (isDesktop1024 || isDesktop1280 || isDesktop1440) {
        return 16;
      }
      return 16;
    };
    $[35] = isDesktop1024;
    $[36] = isDesktop1280;
    $[37] = isDesktop1440;
    $[38] = isMobile;
    $[39] = isTablet;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const getMarginXAxis = t12;
  let t13;
  if ($[41] !== isLessMobile || $[42] !== isTablet) {
    t13 = () => {
      if (isTablet) {
        return 12;
      }
      if (isLessMobile) {
        return 8;
      }
      return 9;
    };
    $[41] = isLessMobile;
    $[42] = isTablet;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const getXAxisFontSize = t13;
  let t14;
  if ($[44] !== isLessMobile || $[45] !== isMobile) {
    t14 = () => {
      if (isLessMobile || isMobile) {
        return 12;
      }
      return 14;
    };
    $[44] = isLessMobile;
    $[45] = isMobile;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const getYAxisFontSize = t14;
  const t15 = getXAxisFontSize();
  let t16;
  if ($[47] !== t15) {
    t16 = {
      fontFamily: "var(--font-open-sans-condensed)",
      textAlign: "center" as const,
      color: "#708390",
      fontWeight: 600,
      fontSize: t15,
      verticalAlign: "top" as const,
      interval: 0
    };
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const xAxisStyles = t16;
  const t17 = !isMobile;
  let t18;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      type: "shadow",
      shadowStyle: {
        color: "#D4D9E1",
        opacity: 0.15
      }
    };
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== isTablet) {
    t19 = point => {
      const margin = isTablet ? 0 : 8;
      const xPos = point[0] + margin;
      const yPos = point[1] + margin;
      return [xPos, yPos];
    };
    $[50] = isTablet;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== isDesktop1024 || $[53] !== isMobile || $[54] !== isTablet || $[55] !== selectedGranularity || $[56] !== selectedMetric || $[57] !== t17 || $[58] !== t19 || $[59] !== year) {
    t20 = {
      borderRadius: 12,
      show: t17,
      trigger: "axis",
      extraCssText: "z-index: 6",
      backgroundColor: "var(--color-card)",
      axisPointer: t18,
      padding: 0,
      borderColor: "var(--color-card)",
      borderWidth: 1,
      position: t19,
      formatter(params) {
        if (params.every(_temp)) {
          return "";
        }
        const filteredParams = params.filter(_temp2);
        const shortAmount = params.length > 10;
        const flexDirection = shortAmount ? "row" : "column";
        const wrap = shortAmount ? "flex-wrap:wrap;" : "";
        const gap = shortAmount ? "16px" : "12px";
        const minMaxValues = {
          tablet: "max-width:300px",
          desktop1024: "max-width:400px",
          default: "min-width:190px;max-width:450px"
        };
        let minMax;
        if (isTablet) {
          minMax = minMaxValues.tablet;
        } else {
          if (isDesktop1024) {
            minMax = minMaxValues.desktop1024;
          } else {
            minMax = minMaxValues.default;
          }
        }
        const maxWithTableValues = {
          tablet: "max-width:190px",
          desktop1024: "max-width:450px",
          default: ""
        };
        let maxWithTable;
        if (isTablet) {
          maxWithTable = maxWithTableValues.tablet;
        } else {
          if (isDesktop1024) {
            maxWithTable = maxWithTableValues.desktop1024;
          } else {
            maxWithTable = maxWithTableValues.default;
          }
        }
        return `
            <div style="border-radius:12px;background-color:var(--color-card);box-shadow:var(--shadow-md);padding:16px;overflow:auto; font-family:var(--font-inter) ,sans-serif">
              <div style="margin-bottom:16px;font-size:12px;font-weight:600;color:#B6BCC2">${selectedGranularity as string === "Annually" ? year : getMonthAbbreviationToolTip(filteredParams[0]?.dataIndex ?? 0)}<span style="display:inline-block;margin-left:4px">${getSelectMetricText(selectedMetric)}</span></div>
              <div style="display:flex;flex-direction:${flexDirection};gap:${gap};${wrap}${minMax}">
                ${filteredParams.reverse().map(item_1 => `<div style="display: flex;align-items:center;gap: 6px;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="${isMobile ? 13 : 16}" height="${isMobile ? 13 : 16}" viewBox="0 0 13 13" fill="none" style="min-width:${isMobile ? 13 : 16};min-height:${isMobile ? 13 : 16}">
                          <circle cx="6.5" cy="6.5" r="5.5" stroke="${item_1.color}" />
                          <circle cx="6.5" cy="6.5" r="4" fill="${item_1.color}" />
                        </svg>
                        <span style="display: inline-block;font-size:14px;color:var(--color-muted-foreground);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;${maxWithTable}"> ${removeBudgetWord(formatBudgetName(item_1.seriesName))}:</span>
                        <span style="font-size:16px;font-weight:700;color:var(--color-foreground)
                  };display: inline-block;">${usLocalizedNumber(item_1.value, 2)}</span>
                      </div>`).join("")}
              </div>
            </div>
      `;
      }
    };
    $[52] = isDesktop1024;
    $[53] = isMobile;
    $[54] = isTablet;
    $[55] = selectedGranularity;
    $[56] = selectedMetric;
    $[57] = t17;
    $[58] = t19;
    $[59] = year;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  const t21 = getHeightGrid();
  const t22 = getTopGrid();
  const t23 = getRightGrid();
  let t24;
  if ($[61] !== t21 || $[62] !== t22 || $[63] !== t23) {
    t24 = {
      height: t21,
      top: t22,
      right: t23
    };
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== granularity || $[66] !== isMobileOrLess) {
    t25 = getChartAxisLabelByGranularity(granularity, isMobileOrLess, false, true);
    $[65] = granularity;
    $[66] = isMobileOrLess;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      show: false
    };
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  let t28;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      show: false,
      symbolOffset: "left",
      lineStyle: {
        color: "transparent"
      }
    };
    t28 = {
      show: false
    };
    $[69] = t27;
    $[70] = t28;
  } else {
    t27 = $[69];
    t28 = $[70];
  }
  const t29 = getMarginXAxis();
  const t30 = isMobile ? 12 : 14;
  let t31;
  if ($[71] !== granularity || $[72] !== isLessMobile || $[73] !== isMobile || $[74] !== t29 || $[75] !== t30 || $[76] !== xAxisStyles || $[77] !== year) {
    let t32;
    if ($[79] !== xAxisStyles) {
      t32 = {
        month: xAxisStyles,
        year: xAxisStyles
      };
      $[79] = xAxisStyles;
      $[80] = t32;
    } else {
      t32 = $[80];
    }
    t31 = {
      margin: t29,
      color: "var(--color-foreground)",
      align: "center",
      fontFamily: "var(--font-open-sans-condensed)",
      fontWeight: 700,
      fontSize: t30,
      baseline: "top",
      interval: 0,
      formatter(value) {
        const formatted = formatterBreakdownChart(granularity, isMobile, year, value, isLessMobile);
        return formatted;
      },
      rich: t32
    };
    $[71] = granularity;
    $[72] = isLessMobile;
    $[73] = isMobile;
    $[74] = t29;
    $[75] = t30;
    $[76] = xAxisStyles;
    $[77] = year;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[81] !== t25 || $[82] !== t31) {
    t32 = {
      show: true,
      type: "category",
      data: t25,
      splitLine: t26,
      axisLine: t27,
      axisTick: t28,
      axisLabel: t31
    };
    $[81] = t25;
    $[82] = t31;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  const t33 = series.length === 0 ? 0 : null;
  const t34 = series.length === 0 ? 1 : null;
  const t35 = getMarginYAxis();
  let t36;
  if ($[84] !== getYAxisFontSize || $[85] !== t35) {
    t36 = {
      show: true,
      fontFamily: "var(--font-open-sans-condensed)",
      margin: t35,
      formatter(value_0, index) {
        if (value_0 === 0 && index === 0) {
          return value_0.toString();
        }
        return replaceAllNumberLetOneBeforeDot(value_0, true);
      },
      color: "var(--color-foreground)",
      fontSize: getYAxisFontSize(),
      fontWeight: 700
    };
    $[84] = getYAxisFontSize;
    $[85] = t35;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  let t38;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      show: false
    };
    t38 = {
      lineStyle: {
        color: "var(--color-border)",
        width: 0.25
      }
    };
    $[87] = t37;
    $[88] = t38;
  } else {
    t37 = $[87];
    t38 = $[88];
  }
  let t39;
  if ($[89] !== t33 || $[90] !== t34 || $[91] !== t36) {
    t39 = {
      min: t33,
      max: t34,
      show: true,
      axisLabel: t36,
      verticalAlign: "middle",
      type: "value",
      zlevel: -1,
      splitNumber: 9,
      axisLine: t37,
      splitLine: t38
    };
    $[89] = t33;
    $[90] = t34;
    $[91] = t36;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== series || $[94] !== t20 || $[95] !== t24 || $[96] !== t32 || $[97] !== t39) {
    t40 = {
      tooltip: t20,
      grid: t24,
      xAxis: t32,
      yAxis: t39,
      series
    };
    $[93] = series;
    $[94] = t20;
    $[95] = t24;
    $[96] = t32;
    $[97] = t39;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  const options = t40;
  let t41;
  let t42;
  if ($[99] !== options || $[100] !== refBreakDownChart) {
    t41 = () => {
      const chartInstance = refBreakDownChart.current?.getEchartsInstance();
      chartInstance?.setOption(options, {
        notMerge: true,
        replaceAnimation: true
      });
    };
    t42 = [options, refBreakDownChart];
    $[99] = options;
    $[100] = refBreakDownChart;
    $[101] = t41;
    $[102] = t42;
  } else {
    t41 = $[101];
    t42 = $[102];
  }
  useEffect(t41, t42);
  let t43;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = cn("relative flex w-full flex-row justify-center", "h-[227px] w-full", "md:h-[268px] md:w-full", "lg:h-[288px] lg:w-full lg:pl-5", "xl:h-[356px] xl:pl-0", "2xl:ml-[-10px] 2xl:h-[356px]");
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  let t44;
  let t45;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      height: "100%",
      width: "100%"
    };
    t45 = {
      renderer: "svg"
    };
    $[104] = t44;
    $[105] = t45;
  } else {
    t44 = $[104];
    t45 = $[105];
  }
  let t46;
  if ($[106] !== options || $[107] !== refBreakDownChart) {
    t46 = <ReactECharts ref={refBreakDownChart} option={options} style={t44} opts={t45} />;
    $[106] = options;
    $[107] = refBreakDownChart;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] !== isLessMobile || $[110] !== showLineYear || $[111] !== year) {
    t47 = showLineYear && <div data-slot="year-x-axis" className={cn("absolute right-[5px] bottom-0 left-[30px] h-[11px]", "rounded-b-sm border-r border-b border-l", "border-border", !isLessMobile && "left-10")}><div data-slot="year-text" className={cn("absolute bottom-[-6px] left-1/2 w-[52px] -translate-x-1/2", "bg-background text-center text-xs font-bold tracking-[1px]", "text-border", "font-(--font-open-sans-condensed)")}>{year}</div></div>;
    $[109] = isLessMobile;
    $[110] = showLineYear;
    $[111] = year;
    $[112] = t47;
  } else {
    t47 = $[112];
  }
  let t48;
  if ($[113] !== t46 || $[114] !== t47) {
    t48 = <div data-slot="chart-container" className={t43}>{t46}{t47}</div>;
    $[113] = t46;
    $[114] = t47;
    $[115] = t48;
  } else {
    t48 = $[115];
  }
  return t48;
}
function _temp2(item_0) {
  return item_0.value !== 0 && Math.abs(item_0.value) > 0.004;
}
function _temp(item) {
  return item.value === 0;
}
export { BreakdownChart };